/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type Category from '@linc-technologies/linced-common/models/goals/category';
import type State from '@linc-technologies/linced-common/models/goals/state';
import type Subcategory from '@linc-technologies/linced-common/models/goals/subcategory';
import type Subject from '@linc-technologies/linced-common/models/goals/subject';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import type OptionsService from '@linc-technologies/linced-common/services/options';
import type StringsService from '@linc-technologies/linced-common/services/strings';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

// Proto: goals.Goal.
export default class Goal extends Model {
  declare [Type]: 'goals/goal';

  @service
  declare options: OptionsService;

  @service
  declare strings: StringsService;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('goals/subject', { async: true, inverse: null })
  declare subjectId: AsyncBelongsTo<Subject>;

  @alias('subjectId')
  declare subject: AsyncBelongsTo<Subject>;

  @attr('string')
  declare subject_id: string | undefined;

  @attr('boolean', { defaultValue: false })
  declare isPLP: boolean;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @belongsTo('goals/category', { async: true, inverse: 'goals' })
  declare categoryId: AsyncBelongsTo<Category>;

  @alias('categoryId')
  declare category: AsyncBelongsTo<Category>;

  @alias('category.id')
  declare category_id: string | null | undefined;

  @belongsTo('goals/subcategory', { async: true, inverse: 'goals' })
  declare subcategoryId: AsyncBelongsTo<Subcategory>;

  @alias('subcategoryId')
  declare subcategory: AsyncBelongsTo<Subcategory>;

  @alias('subcategory.id')
  declare subcategory_id: string | null | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number')
  declare createTime: number | undefined;

  @attr('boolean', { defaultValue: false })
  declare isLocked: boolean;

  @attr('string', { defaultValue: '' })
  declare templateId: string;

  @attr('number', { defaultValue: 0 })
  declare sequence: number;

  @attr('string', { defaultValue: '' })
  declare label: string;

  @attr('string', { defaultValue: '' })
  declare key: string;

  @attr('string', { defaultValue: '' })
  declare educatorDescription: string;

  @attr('string', { defaultValue: '' })
  declare peopleDescription: string;

  @attr('string', { defaultValue: '' })
  declare exemplar: string;

  @hasMany('goals/state', { async: true, inverse: 'goalId' })
  declare states: AsyncHasMany<State>;

  @hasMany('goals/goal', { async: true, inverse: null })
  declare linkedGoalIds: AsyncHasMany<Goal>;

  @alias('linkedGoalIds')
  declare linkedGoals: AsyncHasMany<Goal>;

  @hasMany('goals/goal', { async: true, inverse: null })
  declare priorLinkedGoalIds: AsyncHasMany<Goal>;

  @alias('priorLinkedGoalIds')
  declare priorLinkedGoals: AsyncHasMany<Goal>;

  @hasMany('goals/goal', { async: true, inverse: null })
  declare futureLinkedGoalIds: AsyncHasMany<Goal>;

  @alias('futureLinkedGoalIds')
  declare futureLinkedGoals: AsyncHasMany<Goal>;

  // Computed property to combine all goal lists
  @computed(
    'linkedGoals.content.[]',
    'priorLinkedGoals.content.[]',
    'futureLinkedGoals.content.[]',
  )
  get combinedGoals() {
    // resolve the async hasMany relationship upfront.
    const uniqueGoals = new Set([
      ...resolved(this.linkedGoals),
      ...resolved(this.priorLinkedGoals),
      ...resolved(this.futureLinkedGoals),
    ]);

    return Array.from(uniqueGoals);
  }

  @computed('linkedGoals.content.[]')
  get coreGoals(): Goal[] {
    // resolve the async hasMany relationship upfront.
    const coreGoals = new Set<Goal>([this, ...resolved(this.linkedGoals)]);

    return Array.from(coreGoals);
  }

  @attr('string', { defaultValue: '' })
  declare learningPurpose: string;

  @attr('string', { defaultValue: '' })
  declare learningIntentions: string;

  @attr('string', { defaultValue: '' })
  declare successCriteria: string;

  @attr('string', { defaultValue: '' })
  declare keyQuestions: string;

  @attr('string', { defaultValue: '' })
  declare learningConnections: string;

  @attr('string', { defaultValue: '' })
  declare assessmentTasks: string;

  @attr('string', { defaultValue: '' })
  declare teachingStrategies: string;

  @attr('string', { defaultValue: '' })
  declare SC1: string;

  @attr('string', { defaultValue: '' })
  declare SC2: string;

  @attr('string', { defaultValue: '' })
  declare SC3: string;

  @attr('string', { defaultValue: '' })
  declare SC4: string;

  @attr('string', { defaultValue: '' })
  declare SC5: string;

  // possibleGrades provides an array of grade metadata objects, using any
  // goal-specific success criteria, falling back to subject-specific success
  // criteria and then the school's default progress judgement terminology.
  @computed('SC1', 'SC2', 'SC3', 'SC4', 'SC5', 'subject.possibleGrades.[]')
  get possibleGrades() {
    const grades: Subject['possibleGrades'] = [];
    const subjectGrades = this.get(
      'subject.possibleGrades',
    ) as Subject['possibleGrades'];
    if (isEmpty(subjectGrades)) {
      return grades;
    }

    const hasAnySuccessCriteria = subjectGrades.some((grade) => {
      const label = (this.get(`SC${grade.value}`) as string | undefined) ?? '';
      return !isEmpty(label.trim());
    });
    if (!hasAnySuccessCriteria) {
      // No custom labels; just use the subject's.
      return subjectGrades;
    }

    subjectGrades.forEach((grade) => {
      const label = (this.get(`SC${grade.value}`) as string | undefined) ?? '';
      if (!isEmpty(label)) {
        // Make a copy so that we're not updating the `string` property twice
        // if the subject also has custom Success Criteria.
        grades.push({
          value: grade.value,
          colourClass: grade.colourClass,
          colour: grade.colour,
          string: label,
        });
      }
    });
    return grades;
  }
}
